import {createSlice} from '@reduxjs/toolkit';
import {getActiveAccountProfileLicenseSuccess} from 'src/openapi/admin/apis/AccountActiveLicenseApi';
import {getAccountHierarchyTreeSuccess} from 'src/openapi/admin/apis/AccountHierarchyApi';
import {getAccountActiveUserSuccess} from 'src/openapi/admin/apis/GetAccountActiveUserApi';
import {getAccountNotesSuccess} from 'src/openapi/admin/apis/GetAccountNotesApi';
import {saveAccountNotesSuccess} from 'src/openapi/admin/apis/SaveAccountNotesApi';

import type {PayloadAction} from '@reduxjs/toolkit';
import type {AccountActiveUserCount} from 'src/openapi/admin/models/AccountActiveUserCount';
import type {AccountHierarchyTree} from 'src/openapi/admin/models/AccountHierarchyTree';
import type {AccountManagerInfo} from 'src/openapi/admin/models/AccountManagerInfo';
import type {AccountNotes} from 'src/openapi/admin/models/AccountNotes';
import type {LicenseShortInfo} from 'src/openapi/admin/models/LicenseShortInfo';
import type {AppState} from 'src/store/typedef';

type AccountDetails = {
    hierarchy: AccountHierarchyTree | null,
    license: LicenseShortInfo | null,
    users: AccountActiveUserCount | null,
    accountManagers: Array<AccountManagerInfo> | null,
    notes: AccountNotes | null,
};

export const initialState: AccountDetails = {
    hierarchy: null,
    license: null,
    users: null,
    accountManagers: null,
    notes: null,
};

export const accountPageSlice = createSlice({
    name: 'accountProfile',
    initialState,
    reducers: {
        clearAccountPage: state => {
            state.hierarchy = null;
            state.license = null;
            state.users = null;
            state.accountManagers = null;
            state.notes = null;
        },
        setAccountTeam: (state, action: PayloadAction<Array<AccountManagerInfo>>) => {
            state.accountManagers = action.payload;
        },
        clearAccountTeam: state => {
            state.accountManagers = null;
        },
        clearAccountLicense: state => {
            state.license = null;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getAccountHierarchyTreeSuccess, (state, action) => {
                state.hierarchy = action.payload.data;
            })
            .addCase(getActiveAccountProfileLicenseSuccess, (state, action) => {
                state.license = action.payload.data;
            })
            .addCase(getAccountActiveUserSuccess, (state, action) => {
                state.users = action.payload.data;
            })
            .addCase(getAccountNotesSuccess, (state, action) => {
                state.notes = action.payload.data;
            })
            .addCase(saveAccountNotesSuccess, (state, action) => {
                state.notes = {value: action.payload.meta.accountNotes.value};
            });
    },
});

export const {
    clearAccountPage,
    setAccountTeam,
    clearAccountTeam,
    clearAccountLicense,
} = accountPageSlice.actions;

export type SelectAccountDetails = (state: AppState) => AccountDetails;
export const selectAccountDetails: SelectAccountDetails = state => state.accountPage.details;

export const isAccountUntracked = (state: AppState) => state.accountPage.details.hierarchy?.current?.isUntracked;
